// Thin typed wrappers for /api/v1. The dev server proxies /api → :8091
// (see vite.config.ts) and the backend sets the session cookie on /me.
//
// All HTTP transport is delegated to @avagency/frontend/lib/api.apiFetch
// (credentials:include, JSON helpers, ApiError with body.detail derivation).
// This file owns ONLY the typed resource interfaces + URL templates + the
// AUTH_INVALIDATE_EVENT layer that bridges 401-from-product-API into a
// useMe refetch.
import { apiFetch, ApiError } from '@avagency/frontend/lib/api';
export const isTerminalStatus = (s) => s === 'done' || s === 'failed' || s === 'cancelled';
// AUTH_INVALIDATE_EVENT is fired on the window any time guardianFetch
// observes a 401 from a NON-auth-endpoint. main.tsx listens for this
// and invalidates the auth query so the app gate re-checks /me. By
// emitting at the fetch layer (rather than QueryCache/MutationCache
// listeners), this covers every path: react-query queries, react-
// query mutations, AND manual async handlers (CSV presign download
// in ArchiveHistory, etc.).
//
// The "skip /auth/me" filter prevents a feedback loop: /me's own 401
// is the SIGNAL that the session is gone, not a downstream consequence
// — invalidating from inside it would refetch immediately and loop.
export const AUTH_INVALIDATE_EVENT = 'guardian:auth:invalidate';
// guardianFetch wraps apiFetch with the AUTH_INVALIDATE_EVENT side
// effect. Catches ApiError(401), dispatches the event (unless the
// 401 came from /auth/me itself), then re-throws so call-site error
// handling stays intact.
//
// Also normalizes the thrown shape: callers can still read
// (err as Error & { status?: number }).status — Error.status was the
// pre-migration contract.
async function guardianFetch(path, init) {
    try {
        return await apiFetch(path, init);
    }
    catch (err) {
        if (err instanceof ApiError) {
            if (err.status === 401 && !path.includes('/api/v1/auth/me')) {
                window.dispatchEvent(new CustomEvent(AUTH_INVALIDATE_EVENT));
            }
            // Decorate with Error.status so legacy call sites that read
            // err.status keep working without an `instanceof ApiError` guard.
            const decorated = err;
            decorated.status = err.status;
            throw decorated;
        }
        throw err;
    }
}
export const fetchDashboard = (integrationID) => guardianFetch(`/api/v1/dashboard?integration_id=${encodeURIComponent(integrationID)}`);
export const fetchInactive = (integrationID) => guardianFetch(`/api/v1/inactive?integration_id=${encodeURIComponent(integrationID)}`);
export const fetchArchiveRuns = () => guardianFetch(`/api/v1/archive-runs`);
export const excludeInactive = (id) => guardianFetch(`/api/v1/inactive/${encodeURIComponent(id)}/exclude`, { method: 'POST' });
// archiveContacts POSTs to /api/v1/archive with an optional client-
// supplied Idempotency-Key header. Pass a stable UUID per user intent
// (e.g. one click of the Archive button) so retries collapse server-
// side via scheduler.Enqueue's ON CONFLICT instead of creating duplicate
// jobs. The header value must be UUID-shaped or the API returns 400.
export const archiveContacts = (integrationID, inactiveIDs, idempotencyKey) => {
    const headers = {};
    if (idempotencyKey)
        headers['Idempotency-Key'] = idempotencyKey;
    return guardianFetch(`/api/v1/archive`, {
        method: 'POST',
        headers,
        json: { integration_id: integrationID, inactive_ids: inactiveIDs },
    });
};
export const fetchJobStatus = (requestID) => guardianFetch(`/api/v1/jobs/${encodeURIComponent(requestID)}`);
export const fetchArchiveRunCSVURL = (runID) => guardianFetch(`/api/v1/archive-runs/${encodeURIComponent(runID)}/csv-url`);
export const fetchIntegrations = () => guardianFetch(`/api/v1/integrations`);
// disconnectIntegration revokes the OAuth credential (upstream +
// locally) for the given integration. Returns 204 No Content on
// success; mutation onSuccess invalidates the integrations query so
// main.tsx re-renders to the ConnectHubSpot onboarding screen.
export const disconnectIntegration = (integrationID) => guardianFetch(`/api/v1/integrations/${encodeURIComponent(integrationID)}/disconnect`, { method: 'POST' });
export const fetchMe = () => guardianFetch(`/api/v1/auth/me`);
// signInURL is what the SignIn page navigates the browser to. NOT a
// fetch — the backend responds with a 302 to the WorkOS AuthKit
// hosted UI, and the browser MUST follow that redirect itself (a
// programmatic fetch would receive the 302 + then not re-render the
// page on the hosted login domain).
export const signInURL = (returnTo = '/') => {
    const q = new URLSearchParams({ return_to: returnTo });
    return `/api/v1/auth/signin?${q.toString()}`;
};
// startHubSpotOAuth kicks off the HubSpot connect flow. POSTs to
// /v1/oauth/hubspot/start; the backend's InjectTenantIntoOAuthStart
// middleware overrides tenant_id from the session, and the platform
// OAuth server returns { authorize_url } which we navigate to.
export const startHubSpotOAuth = async (returnTo = '/') => {
    const resp = await guardianFetch(`/v1/oauth/hubspot/start`, { method: 'POST', json: { return_url: returnTo } });
    window.location.href = resp.authorize_url;
};
// parseOAuthCallbackStatus is a PURE read of the current URL's query —
// no side effects, safe to call from useMemo or useState initializers
// (including under React.StrictMode which double-invokes them in dev).
export const parseOAuthCallbackStatus = () => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('av_oauth_status');
    if (!status)
        return { kind: 'none' };
    const errMsg = params.get('av_oauth_error') ?? '';
    if (status === 'success')
        return { kind: 'success' };
    return { kind: 'error', message: errMsg || 'OAuth flow failed' };
};
// clearOAuthCallbackStatusFromURL strips av_oauth_status +
// av_oauth_error from the current URL's query. Side-effecting; must
// be called from a useEffect (so StrictMode's double-invocation of
// render-phase closures doesn't strip the URL before the parse can
// read it). Preserves any non-OAuth query and the hash.
export const clearOAuthCallbackStatusFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('av_oauth_status') && !params.has('av_oauth_error')) {
        return;
    }
    params.delete('av_oauth_status');
    params.delete('av_oauth_error');
    const search = params.toString();
    const cleanURL = window.location.pathname +
        (search ? `?${search}` : '') +
        window.location.hash;
    window.history.replaceState({}, '', cleanURL);
};
// signOut clears the local session cookie + revokes the WorkOS
// session upstream. Browser-redirect-style: the backend responds 204
// + Location header pointing at the post-signout target; we follow
// the Location client-side because fetch doesn't honour 204 redirects.
export const signOut = async (returnTo = '/') => {
    const q = new URLSearchParams({ return_to: returnTo });
    const res = await fetch(`/api/v1/auth/signout?${q.toString()}`, {
        method: 'POST',
        credentials: 'include',
    });
    // Whatever the server says, navigate the browser to the configured
    // landing path; the cookie is already cleared by Set-Cookie in the
    // response.
    const location = res.headers.get('Location') || returnTo;
    window.location.href = location;
};
// Re-export ApiError so call sites can do `instanceof ApiError` without
// a direct kit import.
export { ApiError };
